import { useMemo } from 'react'
import type { JSX } from 'react'
import { buildApplyPlan, draftFlags } from '../../analysis/draft'
import type { AnalysisDraft } from '../../analysis/draft'
import { formatTime } from '../../lib/format'
import { AnalysisLines } from './AnalysisLines'
import { JoinPanel } from './JoinPanel'
import { SpeakerRoles } from './SpeakerRoles'

export type AnalysisReviewProps = {
  draft: AnalysisDraft
  videoDuration: number
  /** Текущая позиция видео — от неё заводится новая строка черновика. */
  currentTime: number
  disabled: boolean
  onChange: (next: AnalysisDraft) => void
  onSeek: (time: number) => void
  /** Свернуть таблицу и вернуться к таймлайну с предпросмотром. */
  onCollapse: () => void
  onDismiss: () => void
}

const LANGUAGE_NAMES: Record<string, string> = {
  ru: 'русский',
  en: 'английский',
  de: 'немецкий',
  fr: 'французский',
  es: 'испанский',
  it: 'итальянский',
  uk: 'украинский',
}

function languageName(code: string): string {
  if (!code) return 'язык не определён'
  return LANGUAGE_NAMES[code] ?? code
}

export function AnalysisReview({
  draft,
  videoDuration,
  currentTime,
  disabled,
  onChange,
  onSeek,
  onCollapse,
  onDismiss,
}: AnalysisReviewProps): JSX.Element {
  const flags = useMemo(() => draftFlags(draft), [draft])
  const suspicious = useMemo(
    () => draft.lines.filter((line) => (flags.get(line.key)?.length ?? 0) > 0).length,
    [draft.lines, flags],
  )
  const plan = useMemo(() => buildApplyPlan(draft, videoDuration), [draft, videoDuration])
  const speech = useMemo(
    () => draft.lines.reduce((sum, line) => sum + Math.max(0, line.end - line.start), 0),
    [draft.lines],
  )
  // Невербальные показываем отдельно: пустой текст у них — норма, и человек
  // должен видеть, что конвейер их вообще нашёл (docs/SEGMENTATION.md).
  const nonverbal = useMemo(
    () => draft.lines.filter((line) => line.kind === 'nonverbal').length,
    [draft.lines],
  )

  return (
    <section className="panel analysis-review">
      <header>
        <h2>Результат разбора</h2>
        <span className="badge" style={{ color: 'var(--warning)' }}>
          черновик
        </span>
        <span className="badge">оригинал: {languageName(draft.language)}</span>
        <span className="badge mono">{draft.lines.length} реплик</span>
        <span className="badge mono">речи {formatTime(speech)}</span>
        {nonverbal > 0 ? (
          <span className="badge nonverbal-badge" title="Крики, стоны, дыхание — реплики без слов">
            ◍ без слов: {nonverbal}
          </span>
        ) : null}
        <span className="spacer" />
        <button type="button" className="ghost" onClick={onCollapse}>
          Свернуть к таймлайну
        </button>
      </header>

      <div className="stack">
        <div className="notice warn">
          <strong>Проверьте перед применением</strong>
          <span className="faint">
            Ошибок здесь заведомо больше, чем видно: диаризация путает похожие голоса,
            расшифровка врёт на именах, перевод — машинный подстрочник. Подсвечено{' '}
            {suspicious} стро{suspicious === 1 ? 'ка' : 'к'} с формальными признаками проблем.
            {plan.dropped > 0
              ? ` Ещё ${plan.dropped} строк(и) не переживут применение: они выходят за пределы видео.`
              : ''}
          </span>
        </div>

        <SpeakerRoles draft={draft} disabled={disabled} onChange={onChange} onSeek={onSeek} />

        <hr />

        <JoinPanel draft={draft} disabled={disabled} onChange={onChange} />

        <hr />

        <AnalysisLines
          draft={draft}
          disabled={disabled}
          currentTime={currentTime}
          videoDuration={videoDuration}
          onChange={onChange}
          onSeek={onSeek}
        />

        <hr />

        <div className="row wrap">
          <button type="button" className="primary" onClick={onCollapse}>
            К таймлайну и применению
          </button>
          <span className="faint">
            Применение — отдельный шаг: пока вы его не подтвердите, в проекте ничего не меняется.
          </span>
          <span className="spacer" />
          <button
            type="button"
            className="danger"
            disabled={disabled}
            onClick={() => {
              if (window.confirm('Отбросить черновик разбора? Правки не сохранятся.')) onDismiss()
            }}
          >
            Отбросить черновик
          </button>
        </div>
      </div>
    </section>
  )
}
